// [2026-09-02] ADDED — the STAFF dashboard shell for proctoring-sdk-backend.
//
// WHY A SECOND SHELL IN THIS REPO, alongside src/App.jsx.
// src/App.jsx renders the client package's `App` and adds five screens built on the OLD
// proctoring-backend gateway — a service the team has stopped using. Rather than rewire those
// screens in place (and lose the record of what they looked like against that API), this is a
// self-contained shell for the live backend. Nothing was deleted; src/App.jsx and its three views
// still exist and still compile.
//
// IT REUSES THE DESIGN SYSTEM, NOT THE DATA LAYER. ui.jsx and charts.jsx come from the client
// package because they are presentation and backend-agnostic — one copy of the design system, both
// apps. `api.js` is local, because the two apps talk to different services.
//
// NAVIGATION IS STATE, NOT A ROUTER — the same reasoning the customer app documents: the backend
// serves only /admin and /admin-static (app/modules/admin/static.py), so /admin/clients would 404 on
// reload. Keeping the view in state means every URL that exists works.
//
// WHAT IS AND IS NOT A BOUNDARY: hiding the Staff nav item from a non-superadmin is CONVENIENCE.
// The boundary is server-side — require_admin on every handler, plus the role re-read from Mongo on
// each request. This shell grants nothing.

import { useCallback, useEffect, useState } from 'react'
import { NavGroup, NavItem } from '@incruiter/proctoring-client-frontend'
import { RangePicker, Skeleton, ToastHost, toast } from '@incruiter/proctoring-client-frontend'
import { admin, getToken, setToken } from './api.js'
// KEPT, not removed — it is the break-glass form, rendered only when `recovering` is set. See the
// note at the `if (!session)` branch below.
import AdminLogin from './AdminLogin.jsx'
// [2026-09-08] ADDED — what an unauthenticated visitor sees now that entry is through InCruiter,
// plus the redirect target and the loop guard that decide whether they see it at all.
import BridgeSignIn, {
  ADMIN_PANEL_URL, INCRUITER_LOGOUT_URL, clearBounced, markBounced, recentlyBounced,
} from './BridgeSignIn.jsx'

// [2026-09-08] ADDED — "hold the page, I am recovering this deployment".
//
// BOTH ?recover=1 AND #recover are accepted. A fragment never reaches a server, which is the safer
// thing to paste into a chat window during an incident; a query parameter is what survives being
// typed from memory. Costing one extra condition to accept either is worth not having someone
// locked out because they guessed the wrong syntax at 2am.
function wantsRecovery() {
  try {
    const { search, hash } = window.location
    return new URLSearchParams(search).get('recover') !== null || hash === '#recover'
  } catch {
    return false
  }
}
import Overview from './views/Overview.jsx'
import Clients from './views/Clients.jsx'
import Billing from './views/Billing.jsx'
import Analytics from './views/Analytics.jsx'
import Diagnostics from './views/Diagnostics.jsx'
import Staff from './views/Staff.jsx'
// [2026-09-07] CHANGED — VideoJobsFeed added: the analysis API's jobs, the staff view of the half
// of the product this dashboard could not see.
// OLD: import { AuditLog, RequestsFeed, SessionsFeed } from './views/Feeds.jsx'
import { AuditLog, RequestsFeed, SessionsFeed, VideoJobsFeed } from './views/Feeds.jsx'

const VIEWS = {
  overview:  { title: 'Platform', blurb: 'Every tenant, all traffic and all money at a glance.' },
  clients:   { title: 'Tenants', blurb: 'Onboard, grant credits, issue keys, suspend.' },
  billing:   { title: 'Billing', blurb: 'What each tenant was granted, spent and still holds.' },
  analytics: { title: 'Analytics', blurb: 'Traffic, latency and failures — platform-wide or per tenant.' },
  // [2026-09-07] CHANGED — the screen now holds BOTH halves of the product, so the blurb says so.
  // OLD: sessions: { title: 'Sessions', blurb: 'Proctoring sessions across every tenant.' },
  sessions:  { title: 'Sessions',
               blurb: 'Browser SDK sittings and submitted video analyses, across every tenant.' },
  requests:  { title: 'API requests', blurb: 'One row per API call — the drill-down for a failure.' },
  audit:     { title: 'Audit log', blurb: 'Who changed what, and when.' },
  health:    { title: 'Diagnostics', blurb: 'Dependencies, flags and limits this environment runs with.' },
  staff:     { title: 'Staff accounts', blurb: 'Named operator accounts (superadmin only).' },
}

// Screens that page over everything, or describe a moment rather than a window, take no time range.
// Listed once here instead of repeated as a condition at each render site.
const RANGELESS = ['sessions', 'requests', 'audit', 'health', 'staff']

const SignOutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
  </svg>
)

export default function AdminApp() {
  const [session, setSession] = useState(null)
  const [booting, setBooting] = useState(true)
  const [view, setView] = useState('overview')
  const [days, setDays] = useState(30)
  // [2026-09-08] ADDED — the break-glass toggle. False means the visitor sees BridgeSignIn; true
  // puts the password form back for the one case it still serves.
  const [recovering, setRecovering] = useState(false)
  // [2026-09-08] ADDED — true when we already sent this visitor to InCruiter and they came back
  // without a session. Changes the wording only; the redirect decision is made in the boot effect.
  const [bounced, setBounced] = useState(false)
  // Set when another screen deep-links into a tenant, so Tenants opens straight on it.
  const [openClientId, setOpenClientId] = useState(null)

  // Resume an existing session on load rather than forcing a sign-in on every refresh.
  //
  // [2026-09-08] CHANGED — ask the server even when sessionStorage is empty.
  //
  // OLD:
  //   if (!getToken()) { setBooting(false); return }
  //   admin.me()
  //     .then((me) => { setSession(me); setBooting(false) })
  //     .catch(() => { setToken(null); setBooting(false) })
  //
  // WHY. That early return is why arriving from the InCruiter admin panel still showed a sign-in
  // form. The operator bridge signs you in by setting an HttpOnly SESSION COOKIE on the API host
  // (proctoring-sdk-backend admin/route.py, _set_session_cookie) — it cannot write sessionStorage,
  // because the cookie is HttpOnly by design and the redirect crosses an origin. So a bridged
  // arrival always had an empty sessionStorage, `getToken()` returned null, and this bailed out
  // before ever asking /auth/me — with a perfectly valid session sitting in the cookie jar.
  //
  // The request already sends `credentials: 'include'` (api.js), and the server already accepts
  // the cookie ahead of the bearer (_token_from), so the fix is simply to let the call happen.
  //
  // COST: one extra request for a genuinely anonymous visitor, which 401s and lands them on the
  // same screen they would have seen anyway. That is the correct trade — the alternative is the
  // only sign-in route this app now has not working.
  //
  // [2026-09-08] CHANGED — an unauthenticated visitor is SENT to InCruiter, not shown a page about
  // it.
  //
  // OLD:
  //   admin.me()
  //     .then((me) => { setSession(me); setBooting(false) })
  //     .catch(() => { setToken(null); setBooting(false) })
  //
  // The screen with a button was still a stop sign in front of the only door. If there is exactly
  // one way in, asking someone to click a link to it is a step with no decision in it.
  //
  // FOUR THINGS HAVE TO BE TRUE FOR THE REDIRECT TO FIRE, and each of the other branches is a real
  // case that a bare `window.location = ADMIN_PANEL_URL` would have broken:
  //
  //   1. /auth/me FAILED. Never redirect a signed-in visitor — that would send someone who just
  //      arrived from the panel straight back to it.
  //   2. RECOVERY WAS NOT REQUESTED. ?recover=1 or #recover holds the page still, which is the only
  //      way to reach the break-glass form once this redirects automatically. Without it, closing
  //      the password door and adding this redirect would together make a deployment with a broken
  //      bridge unrecoverable from a browser.
  //   3. WE KNOW WHERE TO SEND THEM. VITE_INCRUITER_ADMIN_URL unset means no redirect and the
  //      static screen, which is why that variable has no guessed default.
  //   4. WE HAVE NOT ALREADY BOUNCED. See recentlyBounced() — the loop guard, and the reason this
  //      is more than three lines.
  //
  // replace(), not assign(): this page is a turnstile, not a destination. Pushing a history entry
  // would make Back from the panel land here, fail /auth/me again and throw them forward — the
  // same trap ProctoringLaunch.jsx hit on the InCruiter side on 2026-09-03.
  //
  // `booting` is deliberately LEFT TRUE on the redirect path so the skeleton stays up until the
  // navigation commits. Setting it false would flash the sign-in screen for a frame — which reads
  // as "it failed, then it did something", and is what makes a redirect feel broken.
  useEffect(() => {
    admin.me()
      .then((me) => {
        // The round trip worked, so any earlier bounce is spent. Clearing it here rather than on a
        // timer means the NEXT time a session genuinely expires, the automatic redirect is
        // available again instead of being suppressed by a note left over from today.
        clearBounced()
        setSession(me)
        setBooting(false)
      })
      .catch(() => {
        setToken(null)

        if (wantsRecovery()) { setRecovering(true); setBooting(false); return }

        if (ADMIN_PANEL_URL && !recentlyBounced()) {
          // MARKED BEFORE THE NAVIGATION, never after: after does not run. The record has to
          // survive into the next page load, and that load may begin before this frame finishes.
          markBounced()
          window.location.replace(ADMIN_PANEL_URL)
          return
        }

        // Either we have nowhere to send them, or we already tried and they came back. Both end on
        // the static screen; only the second gets the "that did not work" wording.
        setBounced(recentlyBounced())
        setBooting(false)
      })
  }, [])

  // [2026-09-08] CHANGED — signing out ends the InCruiter session too, and leaves the browser there.
  //
  // OLD:
  //   const signOut = useCallback(() => {
  //     admin.logout().catch(() => {})
  //     setToken(null)
  //     setSession(null)
  //     setView('overview')
  //   }, [])
  //
  // TWO THINGS WERE WRONG WITH IT.
  //
  // 1. IT NEVER NAVIGATED. It only flipped React state, so the app re-rendered the "Sign in from
  //    InCruiter" card in place. The automatic redirect lives in the boot effect, and a state change
  //    does not re-run it — so sign-out was the one route to that screen that never redirected.
  //    Reported 2026-09-08.
  //
  // 2. IT DID NOT SIGN ANYONE OUT OF ANYTHING DURABLE. This console has no identity of its own; its
  //    session is minted from an InCruiter one. Clearing the local session while the InCruiter
  //    session stays live means clicking InC-Proctor signs you straight back in with no prompt.
  //    A sign-out that one click undoes is theatre, and on a shared machine it is worse than
  //    theatre — the next person is one click from every tenant's data.
  //
  // ORDER MATTERS AND IS THE SECURITY-RELEVANT PART: our own session is revoked FIRST, and awaited,
  // before the browser leaves for InCruiter. A fire-and-forget request is cancellable by the
  // navigation that follows it, which would leave the proctoring cookie alive on a machine whose
  // user believes they signed out.
  //
  // BOUNDED WAIT, because "signed out" must not depend on the API being up. If the call has not
  // finished in two seconds we proceed anyway: the local session is gone anyway, and the InCruiter
  // logout is the one that stops a re-entry.
  //
  // HONEST LIMITATION: this is a FRONT-CHANNEL logout. If the browser never reaches
  // InCruiter's /logout — the tab is closed mid-navigation, the network drops — the InCruiter
  // session survives. Ending it from the server side is not possible here: this app never sees an
  // InCruiter token, only its own cookie.
  const signOut = useCallback(async () => {
    try {
      await Promise.race([
        admin.logout().catch(() => {}),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ])
    } catch { /* the local session is cleared either way, below */ }

    setToken(null)
    setSession(null)
    setView('overview')

    // replace(), not assign(): a signed-out user pressing Back must not land on a console that
    // still has their last screen in the bfcache.
    if (INCRUITER_LOGOUT_URL) window.location.replace(INCRUITER_LOGOUT_URL)
    // If there is no derivable InCruiter panel we simply stay, cleared, on the sign-in card. Worse,
    // never wrong — and it is the same fallback the sign-in redirect uses.
  }, [])

  // A 401 anywhere means the session died mid-use. Drop to the login screen once, centrally, rather
  // than letting each panel render its own "unauthorised" error.
  const onError = useCallback((err) => {
    if (err?.status === 401) {
      setToken(null)
      setSession(null)
      toast('Your session expired. Please sign in again.', true)
    }
  }, [])

  const openClient = useCallback((clientId) => {
    setOpenClientId(clientId)
    setView('clients')
  }, [])

  if (booting) {
    return <div className="login-wrap"><Skeleton height={120} style={{ width: 380 }} /></div>
  }
  if (!session) {
    // [2026-09-08] CHANGED — the password form is no longer what an unauthenticated visitor sees.
    //
    // OLD:
    //   return <><AdminLogin onSignedIn={...} /><ToastHost /></>
    //
    // WHY: entry to this console is through the InCruiter admin panel now. A form that the server
    // will refuse for every named account is worse than no form — it invites people to type a
    // credential and then tells them it is wrong. BridgeSignIn.jsx has the full reasoning.
    //
    // AdminLogin IS STILL RENDERED, just not first: `recovering` puts it back for the one case it
    // still serves — a deployment whose bridge is broken, signed in with `operator` +
    // GATEWAY_OPERATOR_SECRET, which the server never gates.
    const signedIn = (result) => setSession({ admin: result.user || result.admin, can: result.can })
    return (
      <>
        {recovering
          ? <AdminLogin onSignedIn={signedIn} />
          : <BridgeSignIn onRecover={() => setRecovering(true)} bounced={bounced} />}
        <ToastHost />
      </>
    )
  }

  const who = session.admin || {}
  const can = session.can || {}
  const meta = VIEWS[view] || VIEWS.overview

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand"><span className="brand-mark">In</span><span>InC-Proctor Staff</span></div>

        <NavGroup label="Platform">
          <NavItem id="overview" icon="platform" view={view} setView={setView}>Platform</NavItem>
          <NavItem id="analytics" icon="analytics" view={view} setView={setView}>Analytics</NavItem>
          <NavItem id="health" icon="health" view={view} setView={setView}>Diagnostics</NavItem>
        </NavGroup>

        <NavGroup label="Customers">
          <NavItem id="clients" icon="clients" view={view} setView={setView}>Tenants</NavItem>
          <NavItem id="billing" icon="credits" view={view} setView={setView}>Billing</NavItem>
        </NavGroup>

        <NavGroup label="Activity">
          <NavItem id="sessions" icon="candidates" view={view} setView={setView}>Sessions</NavItem>
          <NavItem id="requests" icon="audit" view={view} setView={setView}>API requests</NavItem>
          <NavItem id="audit" icon="audit" view={view} setView={setView}>Audit log</NavItem>
        </NavGroup>

        {/* Convenience only — the server refuses /admin/v1/users to a non-superadmin regardless. */}
        {can.manage_admins && (
          <NavGroup label="Administration">
            <NavItem id="staff" icon="clients" view={view} setView={setView}>Staff accounts</NavItem>
          </NavGroup>
        )}

        <div className="sidebar-foot">
          <div style={{ padding: '0 8px 10px' }}>
            <div className="who-email">{who.email}</div>
            <div className="muted" style={{ fontSize: 11.5 }}>
              {who.role}{who.bootstrap ? ' · bootstrap' : ''}
            </div>
          </div>
          <button className="nav-item" onClick={signOut}><SignOutIcon />Sign out</button>
        </div>
      </aside>

      <main className="main">
        <div className="page-head">
          <div>
            <h1>{meta.title}</h1>
            <p>{meta.blurb}</p>
          </div>
          {!RANGELESS.includes(view) && <RangePicker value={days} onChange={setDays} />}
        </div>

        {/* The bootstrap session is a SHARED secret, and every write it makes is audited as
            "operator-bootstrap" rather than as a person. Saying so on every screen is the nudge that
            gets named accounts created. */}
        {who.bootstrap && (
          <div className="muted" style={{ fontSize: 12.5, marginBottom: 12 }}>
            Signed in through the bootstrap operator door — actions are audited as
            “operator-bootstrap”, not as a named person.
          </div>
        )}

        {view === 'overview' && <Overview days={days} onError={onError} onOpenClient={openClient} />}
        {view === 'clients' && (
          <Clients days={days} onError={onError} openClientId={openClientId}
                   onCloseClient={() => setOpenClientId(null)} />
        )}
        {view === 'billing' && <Billing days={days} onError={onError} onOpenClient={openClient} />}
        {view === 'analytics' && <Analytics days={days} onError={onError} />}
        {view === 'sessions' && <SessionTabs onError={onError} />}
        {view === 'requests' && <RequestsFeed onError={onError} />}
        {view === 'audit' && <AuditLog onError={onError} />}
        {view === 'health' && <Diagnostics onError={onError} />}
        {view === 'staff' && <Staff onError={onError} canManage={Boolean(can.manage_admins)} />}
      </main>

      <ToastHost />
    </div>
  )
}


// [2026-09-07] ADDED — the two halves of the product, side by side.
//
// A TAB, NOT AN EIGHTH NAV ITEM, and the same choice the customer dashboard made on its Reports
// screen. Both tabs answer one question — "what work has been done for this tenant?" — and the two
// answers are compared far more often than either is read alone. A second nav item would put them
// two clicks apart and make the sidebar longer for no gain.
//
// THE LABELS ARE THE CUSTOMER'S. "SDK Sessions" and "Proctoring Sessions" are what the customer
// dashboard calls them, which is what a customer says on a support call. Staff naming these
// differently would mean translating on every ticket.
//
// MOUNTED CONDITIONALLY, so the tab that is not open issues no request. Rendering both and hiding
// one would double every page load of this screen for data most people never look at.
function SessionTabs({ onError }) {
  const [tab, setTab] = useState('sdk')

  const TABS = [
    { id: 'sdk', label: 'SDK Sessions',
      hint: 'Sittings recorded by the browser SDK on the candidate\u2019s exam page' },
    { id: 'video', label: 'Proctoring Sessions',
      hint: 'Recordings submitted to the proctoring engine for analysis' },
  ]

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {TABS.map((item) => (
          <button key={item.id}
                  className={`btn${tab === item.id ? '' : ' ghost'}`}
                  aria-pressed={tab === item.id}
                  title={item.hint}
                  onClick={() => setTab(item.id)}>
            {item.label}
          </button>
        ))}
      </div>
      {tab === 'sdk' && <SessionsFeed onError={onError} />}
      {tab === 'video' && <VideoJobsFeed onError={onError} />}
    </>
  )
}
